import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { planningAPI, vehiclesAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Truck, 
  Plus, 
  Loader2, 
  Download,
  Edit,
  Trash2,
  Eye,
  Upload,
  File,
  FileSpreadsheet,
  Filter,
  X,
  ClipboardPaste,
  AlertTriangle,
  CheckCircle,
  Search,
  Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format number with commas
const formatNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString('en-ZA', { maximumFractionDigits: 2 });
};

// Parse pasted data (tab-separated from Excel)
// Expected column order (20 columns):
// DATE, TRUCK REGISTRATION, TRAILER 1 REGISTRATION, TRAILER 2 REGISTRATION, LEGS,
// TRUCKTYPE, STATUS, ROUTE, CONTAINER NUMBER 1, VGM 1, CONTAINER NUMBER 2, VGM 2,
// CONTAINER NUMBER 3, VGM 3, MONTH, CLIENT, CONTAINER TYPE, ORDER NUMBER, COST, INVOICE RATE
const parsePastedData = (text) => {
  const lines = text.trim().split('\n');
  if (lines.length === 0) return [];

  // Detect header row (first cell DATE or LOAD DATE)
  const firstCell = (lines[0].split('\t')[0] || '').trim().toUpperCase();
  const hasHeader = firstCell === 'DATE' || firstCell === 'LOAD DATE';

  const dataLines = hasHeader ? lines.slice(1) : lines;
  const records = [];

  const num = (v) => {
    if (v === undefined || v === null || String(v).trim() === '') return '';
    const n = parseFloat(String(v).replace(/,/g, '').trim());
    return isNaN(n) ? '' : n;
  };

  for (const line of dataLines) {
    const values = line.split('\t');
    // Need at least DATE + TRUCK to make a meaningful row
    if ((values[0] || '').trim() === '' && (values[1] || '').trim() === '') continue;

    records.push({
      load_date: (values[0] || '').trim(),
      truck_registration: (values[1] || '').trim(),
      trailer_1_registration: (values[2] || '').trim(),
      trailer_2_registration: (values[3] || '').trim(),
      legs: (values[4] || '').trim(),
      truck_type: (values[5] || '').trim(),
      status: (values[6] || '').trim(),
      route: (values[7] || '').trim(),
      container_number_1: (values[8] || '').trim(),
      vgm_1: num(values[9]),
      container_number_2: (values[10] || '').trim(),
      vgm_2: num(values[11]),
      container_number_3: (values[12] || '').trim(),
      vgm_3: num(values[13]),
      month: (values[14] || '').trim(),
      client: (values[15] || '').trim(),
      container_type: (values[16] || '').trim(),
      order_number: (values[17] || '').trim(),
      cost: num(values[18]),
      invoice_rate: num(values[19]),
    });
  }

  return records;
};

// Paste Import Dialog
const PasteImportDialog = ({ open, onClose, onImport }) => {
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (pasteText) {
      const parsed = parsePastedData(pasteText);
      setPreview(parsed.slice(0, 5)); // Show first 5 rows as preview
    } else {
      setPreview([]);
    }
  }, [pasteText]);

  const handleImport = async () => {
    const records = parsePastedData(pasteText);
    if (records.length === 0) {
      toast.error('No valid data found to import');
      return;
    }
    
    setLoading(true);
    try {
      await onImport(records);
      setPasteText('');
      setPreview([]);
      onClose();
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardPaste className="w-5 h-5 text-blue-500" />
            Paste Data from Excel
          </DialogTitle>
          <DialogDescription>
            Copy the 20 columns from your Excel (in this order): DATE, TRUCK REGISTRATION, TRAILER 1 REGISTRATION, TRAILER 2 REGISTRATION, LEGS, TRUCKTYPE, STATUS, ROUTE, CONTAINER NUMBER 1, VGM 1, CONTAINER NUMBER 2, VGM 2, CONTAINER NUMBER 3, VGM 3, MONTH, CLIENT, CONTAINER TYPE, ORDER NUMBER, COST, INVOICE RATE
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Paste Excel Data</Label>
            <Textarea
              placeholder="Paste 20 tab-separated columns from Excel... Order: DATE, TRUCK REG, TRAILER 1, TRAILER 2, LEGS, TRUCKTYPE, STATUS, ROUTE, CONTAINER 1, VGM 1, CONTAINER 2, VGM 2, CONTAINER 3, VGM 3, MONTH, CLIENT, CONTAINER TYPE, ORDER #, COST, INVOICE RATE"
              rows={8}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          {preview.length > 0 && (
            <div className="space-y-2">
              <Label>Preview ({preview.length} rows shown, {parsePastedData(pasteText).length} total)</Label>
              <div className="border rounded-lg overflow-x-auto max-h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 sticky top-0">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Truck</TableHead>
                      <TableHead className="text-xs">Trailer 1</TableHead>
                      <TableHead className="text-xs">Trailer 2</TableHead>
                      <TableHead className="text-xs">Legs</TableHead>
                      <TableHead className="text-xs">Truck Type</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs">Route</TableHead>
                      <TableHead className="text-xs">Cont 1</TableHead>
                      <TableHead className="text-xs">VGM 1</TableHead>
                      <TableHead className="text-xs">Cont 2</TableHead>
                      <TableHead className="text-xs">VGM 2</TableHead>
                      <TableHead className="text-xs">Cont 3</TableHead>
                      <TableHead className="text-xs">VGM 3</TableHead>
                      <TableHead className="text-xs">Month</TableHead>
                      <TableHead className="text-xs">Client</TableHead>
                      <TableHead className="text-xs">Cont Type</TableHead>
                      <TableHead className="text-xs">Order #</TableHead>
                      <TableHead className="text-xs">Cost</TableHead>
                      <TableHead className="text-xs">Inv Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs whitespace-nowrap">{row.load_date}</TableCell>
                        <TableCell className="text-xs font-mono whitespace-nowrap">{row.truck_registration}</TableCell>
                        <TableCell className="text-xs font-mono whitespace-nowrap">{row.trailer_1_registration}</TableCell>
                        <TableCell className="text-xs font-mono whitespace-nowrap">{row.trailer_2_registration}</TableCell>
                        <TableCell className="text-xs">{row.legs}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.truck_type}</TableCell>
                        <TableCell className="text-xs">{row.status}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.route}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.container_number_1}</TableCell>
                        <TableCell className="text-xs text-right">{row.vgm_1 !== '' ? formatNumber(row.vgm_1) : ''}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.container_number_2}</TableCell>
                        <TableCell className="text-xs text-right">{row.vgm_2 !== '' ? formatNumber(row.vgm_2) : ''}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.container_number_3}</TableCell>
                        <TableCell className="text-xs text-right">{row.vgm_3 !== '' ? formatNumber(row.vgm_3) : ''}</TableCell>
                        <TableCell className="text-xs">{row.month}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.client}</TableCell>
                        <TableCell className="text-xs">{row.container_type}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.order_number}</TableCell>
                        <TableCell className="text-xs text-right">{row.cost !== '' ? formatNumber(row.cost) : ''}</TableCell>
                        <TableCell className="text-xs text-right">{row.invoice_rate !== '' ? formatNumber(row.invoice_rate) : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleImport} 
            disabled={loading || parsePastedData(pasteText).length === 0}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Import {parsePastedData(pasteText).length} Records
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Excel Upload Dialog
const ExcelUploadDialog = ({ open, onClose, onUpload }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    
    setLoading(true);
    try {
      await onUpload(file);
      setFile(null);
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-green-500" />
            Upload Excel File
          </DialogTitle>
          <DialogDescription>
            Upload an Excel file with planning data. Download the template first if needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => planningAPI.downloadTemplate()}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </div>

          <div 
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0])}
              className="hidden"
            />
            <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            {file ? (
              <p className="text-sm font-medium">{file.name}</p>
            ) : (
              <p className="text-sm text-slate-500">Click to select Excel file (.xlsx)</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleUpload} disabled={loading || !file}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Upload & Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Record Dialog
const EMPTY_FORM = {
  load_date: '',
  truck_registration: '',
  trailer_1_registration: '',
  trailer_2_registration: '',
  legs: '',
  truck_type: '',
  status: '',
  route: '',
  container_number_1: '',
  vgm_1: '',
  container_number_2: '',
  vgm_2: '',
  container_number_3: '',
  vgm_3: '',
  month: '',
  client: '',
  container_type: '',
  order_number: '',
  cost: '',
  invoice_rate: '',
};

const EditRecordDialog = ({ open, onClose, record, onSave, vehicles }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (record) {
      setFormData({
        load_date: record.load_date?.slice(0, 10) || '',
        truck_registration: record.truck_registration || '',
        trailer_1_registration: record.trailer_1_registration || record.trailer_registration || '',
        trailer_2_registration: record.trailer_2_registration || '',
        legs: record.legs || '',
        truck_type: record.truck_type || '',
        status: record.status || '',
        route: record.route || '',
        container_number_1: record.container_number_1 || record.reference_number || '',
        vgm_1: record.vgm_1 ?? record.vgm ?? '',
        container_number_2: record.container_number_2 || '',
        vgm_2: record.vgm_2 ?? '',
        container_number_3: record.container_number_3 || '',
        vgm_3: record.vgm_3 ?? '',
        month: record.month || '',
        client: record.client || '',
        container_type: record.container_type || '',
        order_number: record.order_number || '',
        cost: record.cost ?? '',
        invoice_rate: record.invoice_rate ?? '',
      });
    } else {
      setFormData({ ...EMPTY_FORM, load_date: new Date().toISOString().slice(0, 10) });
    }
  }, [record, open]);

  const handleSave = async () => {
    const errs = [];
    if (!formData.load_date) errs.push('Date');
    if (!formData.truck_registration) errs.push('Truck Registration');
    if (!formData.trailer_1_registration && !formData.trailer_2_registration) errs.push('at least one Trailer');
    const atLeastOneVgm = [formData.vgm_1, formData.vgm_2, formData.vgm_3].some(v => v !== '' && parseFloat(v) > 0);
    if (!atLeastOneVgm) errs.push('at least one VGM');
    if (formData.cost === '' || formData.cost === null || formData.cost === undefined) errs.push('Cost (can be 0)');
    if (formData.invoice_rate === '' || formData.invoice_rate === null || formData.invoice_rate === undefined) errs.push('Invoice Rate (can be 0)');
    if (errs.length) {
      toast.error('Missing: ' + errs.join(', '));
      return;
    }

    // Normalise payload — convert blanks to 0 for numeric columns
    const num = (v) => v === '' || v === null || v === undefined ? 0 : parseFloat(v) || 0;
    const payload = {
      ...formData,
      vgm_1: num(formData.vgm_1),
      vgm_2: num(formData.vgm_2),
      vgm_3: num(formData.vgm_3),
      cost: num(formData.cost),
      invoice_rate: num(formData.invoice_rate),
    };

    setLoading(true);
    try {
      await onSave(record?.id, payload);
      onClose();
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const trucks = vehicles.filter(v => 
    v.vehicle_type?.toLowerCase() !== 'trailer'
  );
  const trailers = vehicles.filter(v => 
    v.vehicle_type?.toLowerCase() === 'trailer'
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-500" />
            {record ? 'Edit Load Record' : 'New Load Record'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.load_date}
                onChange={(e) => setFormData({ ...formData, load_date: e.target.value })}
                data-testid="plan-load-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Truck Registration *</Label>
              <Select
                value={formData.truck_registration || 'manual'}
                onValueChange={(v) => setFormData({ ...formData, truck_registration: v === 'manual' ? '' : v })}
              >
                <SelectTrigger><SelectValue placeholder="Select truck" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Type manually...</SelectItem>
                  {trucks.map((v) => (
                    <SelectItem key={v.id} value={v.registration_number}>{v.registration_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!trucks.some(t => t.registration_number === formData.truck_registration) && (
                <Input
                  value={formData.truck_registration}
                  onChange={(e) => setFormData({ ...formData, truck_registration: e.target.value.toUpperCase() })}
                  placeholder="NP 12345"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Trailer 1 Registration</Label>
              <Select
                value={formData.trailer_1_registration || 'none'}
                onValueChange={(v) => setFormData({ ...formData, trailer_1_registration: (v === 'none' || v === 'manual') ? '' : v })}
              >
                <SelectTrigger><SelectValue placeholder="Select trailer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Type manually...</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                  {trailers.map((v) => (
                    <SelectItem key={v.id} value={v.registration_number}>{v.registration_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.trailer_1_registration && !trailers.some(t => t.registration_number === formData.trailer_1_registration) && (
                <Input
                  value={formData.trailer_1_registration}
                  onChange={(e) => setFormData({ ...formData, trailer_1_registration: e.target.value.toUpperCase() })}
                  placeholder="TR 001"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Trailer 2 Registration</Label>
              <Select
                value={formData.trailer_2_registration || 'none'}
                onValueChange={(v) => setFormData({ ...formData, trailer_2_registration: (v === 'none' || v === 'manual') ? '' : v })}
              >
                <SelectTrigger><SelectValue placeholder="Select trailer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Type manually...</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                  {trailers.map((v) => (
                    <SelectItem key={v.id} value={v.registration_number}>{v.registration_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.trailer_2_registration && !trailers.some(t => t.registration_number === formData.trailer_2_registration) && (
                <Input
                  value={formData.trailer_2_registration}
                  onChange={(e) => setFormData({ ...formData, trailer_2_registration: e.target.value.toUpperCase() })}
                  placeholder="TR 002"
                />
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Legs</Label>
              <Input value={formData.legs} onChange={(e) => setFormData({ ...formData, legs: e.target.value })} placeholder="1" />
            </div>
            <div className="space-y-2">
              <Label>Truck Type</Label>
              <Input value={formData.truck_type} onChange={(e) => setFormData({ ...formData, truck_type: e.target.value })} placeholder="Triaxle" />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Input value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} placeholder="Delivered" />
            </div>
            <div className="space-y-2">
              <Label>Route</Label>
              <Input value={formData.route} onChange={(e) => setFormData({ ...formData, route: e.target.value })} placeholder="JHB-DBN" />
            </div>
          </div>

          {/* Containers (VGM total = sum) */}
          <div className="border rounded-lg p-4 bg-slate-50">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              Containers & VGM
              <span className="text-xs font-normal text-slate-500">
                (Total VGM = {formatNumber([formData.vgm_1, formData.vgm_2, formData.vgm_3].reduce((s, v) => s + (parseFloat(v) || 0), 0))} kg)
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Label>Container Number {i}{i === 1 ? ' *' : ''}</Label>
                  <Input
                    value={formData[`container_number_${i}`]}
                    onChange={(e) => setFormData({ ...formData, [`container_number_${i}`]: e.target.value })}
                    placeholder={i === 1 ? 'CONT123456' : 'optional'}
                  />
                  <Label className="text-xs text-slate-600">VGM {i} (kg)</Label>
                  <Input
                    type="number"
                    value={formData[`vgm_${i}`]}
                    onChange={(e) => setFormData({ ...formData, [`vgm_${i}`]: e.target.value })}
                    placeholder="15000"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Month</Label>
              <Input value={formData.month} onChange={(e) => setFormData({ ...formData, month: e.target.value })} placeholder="Auto-filled from Date if blank" />
            </div>
            <div className="space-y-2">
              <Label>Client</Label>
              <Input value={formData.client} onChange={(e) => setFormData({ ...formData, client: e.target.value })} placeholder="ABC Logistics" />
            </div>
            <div className="space-y-2">
              <Label>Container Type</Label>
              <Input value={formData.container_type} onChange={(e) => setFormData({ ...formData, container_type: e.target.value })} placeholder="20FT" />
            </div>
            <div className="space-y-2">
              <Label>Order Number</Label>
              <Input value={formData.order_number} onChange={(e) => setFormData({ ...formData, order_number: e.target.value })} placeholder="ORD-001" />
            </div>
          </div>

          {/* Row 4 — Cost & Invoice */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cost * <span className="text-xs text-slate-500">(can be 0)</span></Label>
              <Input
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice Rate * <span className="text-xs text-slate-500">(can be 0)</span></Label>
              <Input
                type="number"
                value={formData.invoice_rate}
                onChange={(e) => setFormData({ ...formData, invoice_rate: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {record ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Document Management Dialog
const DocumentDialog = ({ open, onClose, record, onUpload, onDelete }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await onUpload(record.id, file);
      toast.success('Document uploaded');
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const documents = record?.documents || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <File className="w-5 h-5 text-blue-500" />
            Documents - {record?.reference_number || record?.truck_registration}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleUpload}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />
            <Button 
              variant="outline" 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload Document
            </Button>
          </div>

          {documents.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No documents uploaded</p>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <File className="w-4 h-4 text-blue-500" />
                    <span className="text-sm">{doc.original_name || doc.filename}</span>
                    <Badge variant="outline" className="text-xs">{doc.document_type}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={`${API_URL}/api/uploads/planning/${doc.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => onDelete(record.id, doc.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
export const PlanningLoadingPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filterTruck, setFilterTruck] = useState('');
  const [filterTrailer, setFilterTrailer] = useState('');
  const [filterLoadType, setFilterLoadType] = useState('');
  const [filterRoute, setFilterRoute] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterHasDocs, setFilterHasDocs] = useState('all');
  const [filterHasError, setFilterHasError] = useState('all');
  const [filterHasComment, setFilterHasComment] = useState('all');
  const [searchText, setSearchText] = useState('');
  
  // Dialogs
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedMonth) params.month = selectedMonth;
      if (filterTruck) params.truck_registration = filterTruck;
      if (filterTrailer) params.trailer_registration = filterTrailer;
      if (filterLoadType) params.load_type = filterLoadType;
      if (filterRoute) params.route = filterRoute;
      if (filterClient) params.client = filterClient;
      if (filterHasDocs === 'yes') params.has_documents = true;
      if (filterHasDocs === 'no') params.has_documents = false;
      if (filterHasError === 'yes') params.has_error = true;
      if (filterHasError === 'no') params.has_error = false;
      if (filterHasComment === 'yes') params.has_comment = true;
      if (filterHasComment === 'no') params.has_comment = false;
      
      const [recordsRes, statsRes, vehiclesRes, monthsRes] = await Promise.all([
        planningAPI.list(params),
        planningAPI.getStatistics(selectedMonth),
        vehiclesAPI.list(),
        planningAPI.getAvailableMonths(),
      ]);
      
      setRecords(recordsRes.data || []);
      setStatistics(statsRes.data || {});
      setVehicles(vehiclesRes.data || []);
      setAvailableMonths(monthsRes.data?.months || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, filterTruck, filterTrailer, filterLoadType, filterRoute, filterClient, filterHasDocs, filterHasError, filterHasComment]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle bulk import from paste
  const handleBulkImport = async (records) => {
    try {
      const result = await planningAPI.bulkImport(records, user.id, `${user.first_name} ${user.last_name}`);
      if (result.data.errors?.length > 0) {
        toast.warning(`Imported ${result.data.imported} records with ${result.data.errors.length} errors`);
      } else {
        toast.success(`Successfully imported ${result.data.imported} records`);
      }
      fetchData();
    } catch (error) {
      toast.error('Import failed');
      throw error;
    }
  };

  // Handle Excel file upload
  const handleExcelUpload = async (file) => {
    try {
      const result = await planningAPI.importExcel(file, user.id, `${user.first_name} ${user.last_name}`);
      if (result.data.errors?.length > 0) {
        toast.warning(`Imported ${result.data.imported} records with ${result.data.errors.length} errors`);
      } else {
        toast.success(`Successfully imported ${result.data.imported} records`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Excel import failed');
      throw error;
    }
  };

  // Handle save (create/update)
  const handleSave = async (id, data) => {
    try {
      if (id) {
        await planningAPI.update(id, data);
        toast.success('Record updated');
      } else {
        await planningAPI.create(data, user.id, `${user.first_name} ${user.last_name}`);
        toast.success('Record created');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to save record');
      throw error;
    }
  };

  // Handle delete
  const handleDelete = async (record) => {
    if (!window.confirm(`Delete load record for ${record.truck_registration}?`)) return;
    
    try {
      await planningAPI.delete(record.id);
      toast.success('Record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (recordId, file) => {
    await planningAPI.uploadDocument(recordId, file);
    fetchData();
  };

  // Handle document delete
  const handleDocumentDelete = async (recordId, docId) => {
    if (!window.confirm('Delete this document?')) return;
    await planningAPI.deleteDocument(recordId, docId);
    fetchData();
    // Refresh selected record
    const updated = await planningAPI.get(recordId);
    setSelectedRecord(updated.data);
  };

  // Filter records by search text
  const filteredRecords = records.filter(r => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      r.truck_registration?.toLowerCase().includes(search) ||
      r.trailer_registration?.toLowerCase().includes(search) ||
      r.reference_number?.toLowerCase().includes(search) ||
      r.client?.toLowerCase().includes(search) ||
      r.route?.toLowerCase().includes(search)
    );
  });

  if (loading && records.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="planning-loading-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            PLANNING & LOADING
          </h1>
          <p className="text-slate-500 mt-1">
            Manage load planning with weight calculations and compliance tracking
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setPasteDialogOpen(true)} className="gap-2">
            <ClipboardPaste className="w-4 h-4" />
            Paste from Excel
          </Button>
          <Button variant="outline" onClick={() => setExcelDialogOpen(true)} className="gap-2">
            <Upload className="w-4 h-4" />
            Upload Excel
          </Button>
          <Button variant="outline" onClick={() => planningAPI.exportExcel({ month: selectedMonth })} className="gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Export
          </Button>
          <Button onClick={() => { setSelectedRecord(null); setEditDialogOpen(true); }} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Record
          </Button>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Loads</p>
              <p className="text-2xl font-bold">{statistics.total_records}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Avg Utilization</p>
              <p className={`text-2xl font-bold ${statistics.avg_utilization > 100 ? 'text-red-600' : 'text-emerald-600'}`}>
                {statistics.avg_utilization?.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Avg Over/Under</p>
              <p className={`text-2xl font-bold ${(statistics.avg_over_under || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {(statistics.avg_over_under || 0) > 0 ? '+' : ''}{statistics.avg_over_under?.toFixed(1)}%
              </p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Overloaded</p>
              <p className="text-2xl font-bold text-red-600">{statistics.overloaded_count}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">With Errors</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.with_errors || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-sm">Filters</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
            <Select value={selectedMonth || 'all'} onValueChange={(v) => setSelectedMonth(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {availableMonths.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Truck..."
              value={filterTruck}
              onChange={(e) => setFilterTruck(e.target.value)}
              className="h-9"
            />
            <Input
              placeholder="Trailer..."
              value={filterTrailer}
              onChange={(e) => setFilterTrailer(e.target.value)}
              className="h-9"
            />
            <Input
              placeholder="Load Type..."
              value={filterLoadType}
              onChange={(e) => setFilterLoadType(e.target.value)}
              className="h-9"
            />
            <Input
              placeholder="Route..."
              value={filterRoute}
              onChange={(e) => setFilterRoute(e.target.value)}
              className="h-9"
            />
            <Input
              placeholder="Client..."
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="h-9"
            />
            <Select value={filterHasDocs} onValueChange={setFilterHasDocs}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Docs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Docs</SelectItem>
                <SelectItem value="yes">Has Docs</SelectItem>
                <SelectItem value="no">No Docs</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterHasError} onValueChange={setFilterHasError}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Errors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Errors</SelectItem>
                <SelectItem value="yes">Has Errors</SelectItem>
                <SelectItem value="no">No Errors</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterHasComment} onValueChange={setFilterHasComment}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Overloaded" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="yes">Overloaded</SelectItem>
                <SelectItem value="no">Not Overloaded</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-9 pl-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold">Date</TableHead>
                  <TableHead className="text-xs font-semibold">Truck</TableHead>
                  <TableHead className="text-xs font-semibold">Trailer 1</TableHead>
                  <TableHead className="text-xs font-semibold">Trailer 2</TableHead>
                  <TableHead className="text-xs font-semibold">Legs</TableHead>
                  <TableHead className="text-xs font-semibold">Truck Type</TableHead>
                  <TableHead className="text-xs font-semibold">Status</TableHead>
                  <TableHead className="text-xs font-semibold">Route</TableHead>
                  <TableHead className="text-xs font-semibold">Containers</TableHead>
                  <TableHead className="text-xs font-semibold">Client</TableHead>
                  <TableHead className="text-xs font-semibold">Cont Type</TableHead>
                  <TableHead className="text-xs font-semibold">Order #</TableHead>
                  <TableHead className="text-xs font-semibold text-right">VGM Total</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Cost</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Inv Rate</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Loaded GCM</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Outcome</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Util %</TableHead>
                  <TableHead className="text-xs font-semibold text-right">+/- %</TableHead>
                  <TableHead className="text-xs font-semibold">Error</TableHead>
                  <TableHead className="text-xs font-semibold">Comment</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Docs</TableHead>
                  <TableHead className="text-xs font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={23} className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500">No records found</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2"
                        onClick={() => setPasteDialogOpen(true)}
                      >
                        Import Data
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRecords.map((record) => {
                    const isOverloaded = (record.over_under_pct || 0) > 0;
                    const hasDocs = record.documents?.length > 0;
                    const hasError = !!record.error;
                    // Build compact container list "CONT1 (15k) / CONT2 (12k)"
                    const containerLabels = [1, 2, 3]
                      .map(i => {
                        const c = record[`container_number_${i}`];
                        const v = record[`vgm_${i}`];
                        if (!c && !v) return null;
                        const vgmLabel = v ? ` (${formatNumber(v)})` : '';
                        return `${c || '—'}${vgmLabel}`;
                      })
                      .filter(Boolean);
                    
                    return (
                      <TableRow 
                        key={record.id}
                        className={`${isOverloaded ? 'bg-red-50' : hasError ? 'bg-amber-50' : ''} hover:bg-slate-50`}
                      >
                        <TableCell className="text-xs">{record.load_date}</TableCell>
                        <TableCell className="text-xs font-mono font-medium">{record.truck_registration}</TableCell>
                        <TableCell className="text-xs font-mono">{record.trailer_1_registration || record.trailer_registration || '-'}</TableCell>
                        <TableCell className="text-xs font-mono">{record.trailer_2_registration || '-'}</TableCell>
                        <TableCell className="text-xs">{record.legs || '-'}</TableCell>
                        <TableCell className="text-xs">{record.truck_type || record.load_type || '-'}</TableCell>
                        <TableCell className="text-xs">{record.status || '-'}</TableCell>
                        <TableCell className="text-xs">{record.route || '-'}</TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate" title={containerLabels.join(' / ')}>
                          {containerLabels.length > 0 ? containerLabels.join(' / ') : (record.reference_number || '-')}
                        </TableCell>
                        <TableCell className="text-xs">{record.client || '-'}</TableCell>
                        <TableCell className="text-xs">{record.container_type || '-'}</TableCell>
                        <TableCell className="text-xs font-mono">{record.order_number || '-'}</TableCell>
                        <TableCell className="text-xs text-right font-medium">{formatNumber(record.vgm)}</TableCell>
                        <TableCell className="text-xs text-right">{formatNumber(record.cost)}</TableCell>
                        <TableCell className="text-xs text-right">{formatNumber(record.invoice_rate)}</TableCell>
                        <TableCell className="text-xs text-right">{formatNumber(record.loaded_gcm)}</TableCell>
                        <TableCell className={`text-xs text-right font-medium ${record.outcome_kg < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {formatNumber(record.outcome_kg)}
                        </TableCell>
                        <TableCell className={`text-xs text-right font-medium ${isOverloaded ? 'text-red-600' : ''}`}>
                          {record.utilization_pct?.toFixed(1)}%
                        </TableCell>
                        <TableCell className={`text-xs text-right font-bold ${isOverloaded ? 'text-red-600' : 'text-emerald-600'}`}>
                          {record.over_under_pct > 0 ? '+' : ''}{record.over_under_pct?.toFixed(1)}%
                        </TableCell>
                        <TableCell className="text-xs max-w-[150px]">
                          {record.error ? (
                            <span className="text-amber-600 font-medium truncate block" title={record.error}>
                              {record.error}
                            </span>
                          ) : (
                            <span className="text-emerald-600">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs max-w-[180px]">
                          {record.comment ? (
                            <span className="text-red-600 font-medium truncate block" title={record.comment}>
                              {record.comment}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {hasDocs ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-500 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => { setSelectedRecord(record); setDocumentDialogOpen(true); }}
                            >
                              <File className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => { setSelectedRecord(record); setEditDialogOpen(true); }}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600"
                              onClick={() => handleDelete(record)}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <PasteImportDialog
        open={pasteDialogOpen}
        onClose={() => setPasteDialogOpen(false)}
        onImport={handleBulkImport}
      />
      <ExcelUploadDialog
        open={excelDialogOpen}
        onClose={() => setExcelDialogOpen(false)}
        onUpload={handleExcelUpload}
      />
      <EditRecordDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSelectedRecord(null); }}
        record={selectedRecord}
        onSave={handleSave}
        vehicles={vehicles}
      />
      <DocumentDialog
        open={documentDialogOpen}
        onClose={() => { setDocumentDialogOpen(false); setSelectedRecord(null); }}
        record={selectedRecord}
        onUpload={handleDocumentUpload}
        onDelete={handleDocumentDelete}
      />
    </div>
  );
};

export default PlanningLoadingPage;
